import { create } from 'zustand';
import { createMMKV, useMMKVString } from 'react-native-mmkv';
import { LoyaltyTermsState, useLoyaltyTerms } from '@/hooks/use-loyalty-terms';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback } from 'react';

const HIDE_BACKSTOP_MS = 1_000;

const useIsClosingStore = create<{
  isClosing: boolean;
  setIsClosing: (isClosing: boolean) => void;
}>((set) => ({
  isClosing: false,
  setIsClosing: (isClosing) => set({ isClosing }),
}));

let backstop: ReturnType<typeof setTimeout> | undefined;

const setIsClosing = (isClosing: boolean) => {
  clearTimeout(backstop);
  backstop = isClosing
    ? setTimeout(() => setIsClosing(false), HIDE_BACKSTOP_MS)
    : undefined;

  useIsClosingStore.getState().setIsClosing(isClosing);
};

// Passed to the modal so it can report the end of its exit animation
export const onLoyaltyModalHidden = () => setIsClosing(false);

export const loyaltyStorage = createMMKV({ id: 'loyalty-storage' });

export const DISMISSAL_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export const dismissedKeyFor = (restaurantUUID: string, termsUUID: string) =>
  `loyaltyTermsDismissed:${restaurantUUID}:${termsUUID}`;

export const isDismissalActive = (storedValue: string | undefined): boolean => {
  if (!storedValue) {
    return false;
  }

  const dismissedAt = Number(storedValue);
  if (!Number.isFinite(dismissedAt)) {
    return false;
  }

  return Date.now() - dismissedAt < DISMISSAL_TTL_MS;
};

interface LoyaltyTermsPrompt extends LoyaltyTermsState {
  dismiss: () => void;
  isBlocking: boolean;
  isVisible: boolean;
}

// Shows when the selected restaurant has a PENDING enrollment with outstanding
// terms for the user to accept. Dismissing snoozes it for DISMISSAL_TTL_MS per
// (restaurant, terms version) and a new version reopens it. The snooze is read
// through useMMKVString, so every caller stays in sync
export const useLoyaltyTermsPrompt = (enabled = true): LoyaltyTermsPrompt => {
  const { selectedRestaurantRelationship } = useAuthenticatedUserContext();
  const restaurantUUID = selectedRestaurantRelationship.uuid;

  const terms = useLoyaltyTerms(
    selectedRestaurantRelationship.supplierUUID,
    undefined,
    !enabled, // Skip
  );

  // The terms version is part of the key, so there's nothing to read yet
  const dismissedKey = terms.latestTermsUUID
    ? dismissedKeyFor(restaurantUUID, terms.latestTermsUUID)
    : '';

  const [dismissedAt, setDismissedAt] = useMMKVString(
    dismissedKey,
    loyaltyStorage,
  );

  const dismiss = useCallback(() => {
    if (dismissedKey) {
      setIsClosing(true);
      setDismissedAt(Date.now().toString());
    }
  }, [dismissedKey, setDismissedAt]);

  const isClosing = useIsClosingStore((state) => state.isClosing);

  const isVisible =
    terms.shouldPromptForTerms && !isDismissalActive(dismissedAt);

  return {
    ...terms,
    dismiss,
    isBlocking: terms.isLoading || isClosing || isVisible,
    isVisible,
  };
};
